/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useEffect,
  useState,
  type ReactElement,
  useContext
} from 'react'
import {
  Box,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Flex,
  FormLabel,
  Heading,
  Icon,
  // Input,
  Link,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
  Select,
  Button
} from '@chakra-ui/react'
import { RoomSettingsContext } from '../pages/+RoomSettingsContext'
import LogoInline from '../components/logo'
import { DownloadIcon } from '@chakra-ui/icons'
import { CSVLink } from 'react-csv'
import { AiFillLinkedin } from '@react-icons/all-files/ai/AiFillLinkedin'
import { AiOutlineTwitter } from '@react-icons/all-files/ai/AiOutlineTwitter'
import { AiOutlineYoutube } from '@react-icons/all-files/ai/AiOutlineYoutube'
import { GiButterfly } from '@react-icons/all-files/gi/GiButterfly'
import { Info } from 'lucide-react'

declare global {
  interface Window {
    cc: any
    openDrawer: any
  }
}

interface SideNavProps {
  download?: any
}

const footerLinks = [
  {
    label: 'About',
    link: '/#about'
  },
  {
    label: 'Privacy Policy',
    link: '/privacy'
  },
  {
    label: 'Cookie Settings',
    onClick: () => {
      ;(window as any)?.CC?.showroomSettings()
    }
  }
]

const socialLinks = [
  {
    name: 'BlueSky',
    icon: GiButterfly,
    link: 'https://bsky.app/profile/reactvts.bsky.social'
  },
  {
    name: 'Twitter',
    icon: AiOutlineTwitter,
    link: 'https://twitter.com/reactvts'
  },
  {
    name: 'Youtube',
    icon: AiOutlineYoutube,
    link: 'https://www.youtube.com/@reactvts'
  },
  {
    name: 'LinkedIn',
    icon: AiFillLinkedin,
    link: 'https://www.linkedin.com/company/reactvts'
  }
]

const SideNav = (props: SideNavProps): ReactElement => {
  const { download = null } = props
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { roomSettings, setRoomSettings } = useContext(RoomSettingsContext)

  useEffect((): void => {
    window.openDrawer = {
      toggle: () => {
        setIsOpen((prevValue) => !prevValue)
      }
    }
  }, [])

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => {
        setIsOpen(false)
      }}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <Logo />
        </DrawerHeader>

        <DrawerBody>
          {roomSettings !== null && (
            <>
              <Heading size="md" mb="10px">
                {roomSettings.name}{' '}
              </Heading>
              {/* <Box>{roomSettings.info}</Box> */}
              <Heading size="md" mb="5px" mt="20px">
                Options
              </Heading>
              {Object.keys(roomSettings.options).map(
                (key: string, index: number): ReactElement => {
                  const item = roomSettings.options[key]
                  if (item.hidden?.(roomSettings) === true) {
                    return <React.Fragment key={index} />
                  }
                  switch (item.type) {
                    case 'bool':
                      return (
                        <Checkbox
                          fontWeight="600"
                          mb="10px"
                          isChecked={item.value}
                          key={key}
                          onChange={(e: any) => {
                            setRoomSettings((prevValue: any) => {
                              prevValue.options[key].value = e.target.checked
                              return { ...prevValue }
                            })
                          }}
                        >
                          {item.label}{' '}
                          {item.tooltip !== undefined && (
                            <Tooltip label={item.tooltip}>
                              <Icon as={Info} />
                            </Tooltip>
                          )}
                        </Checkbox>
                      )
                    case 'numberWithToggle':
                      return (
                        <React.Fragment key={key}>
                          <FormLabel fontWeight={600}>
                            <Checkbox
                              mb="10px"
                              isChecked={item.active}
                              key={key}
                              onChange={(e: any) => {
                                setRoomSettings((prevValue: any) => {
                                  prevValue.options[key].active =
                                    e.target.checked
                                  return { ...prevValue }
                                })
                              }}
                            >
                              {item.label}{' '}
                              {item.tooltip !== undefined && (
                                <Tooltip label={item.tooltip}>
                                  <Icon as={Info} />
                                </Tooltip>
                              )}
                            </Checkbox>
                          </FormLabel>
                          <NumberInput
                            step={item.step ?? 5}
                            defaultValue={item.value}
                            min={item.min ?? 0}
                            max={item.max ?? null}
                            onChange={(value: any) => {
                              setRoomSettings((prevValue: any) => {
                                prevValue.options[key].value = parseInt(value)
                                return { ...prevValue }
                              })
                            }}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </React.Fragment>
                      )
                    case 'inlineNumberWithToggle':
                      return (
                        <React.Fragment key={key}>
                          <FormLabel as="div" key={key} fontWeight={600}>
                            <Checkbox
                              mb="10px"
                              isChecked={item.active}
                              key={key}
                              onChange={(e: any) => {
                                setRoomSettings((prevValue: any) => {
                                  prevValue.options[key].active =
                                    e.target.checked
                                  return { ...prevValue }
                                })
                              }}
                            >
                              {item.label.split('{X}')[0]}
                              <Box
                                as={NumberInput}
                                step={item.step ?? 5}
                                defaultValue={item.value}
                                min={item.min ?? 0}
                                max={item.max ?? null}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  e.preventDefault()
                                }}
                                display="inline-flex"
                                w={item.max > 99 ? '5.5em' : '4.5em'}
                                onChange={(value: any) => {
                                  setRoomSettings((prevValue: any) => {
                                    prevValue.options[key].value =
                                      parseInt(value)
                                    return { ...prevValue }
                                  })
                                }}
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </Box>
                              {item.label.split('{X}')[1]}{' '}
                              {item.tooltip !== undefined && (
                                <Tooltip label={item.tooltip}>
                                  <Icon as={Info} />
                                </Tooltip>
                              )}
                            </Checkbox>
                          </FormLabel>
                        </React.Fragment>
                      )
                    case 'number':
                      return (
                        <FormLabel key={key} fontWeight={600}>
                          {item.label}{' '}
                          {item.tooltip !== undefined && (
                            <Tooltip label={item.tooltip}>
                              <Icon as={Info} />
                            </Tooltip>
                          )}
                          <NumberInput
                            mb="10px"
                            step={item.step ?? 5}
                            defaultValue={item.value}
                            min={item.min ?? 0}
                            max={item.max ?? null}
                            onChange={(value: any) => {
                              const tempValue = parseInt(value)
                              const min = item.min ?? 0
                              const max = item.max ?? Infinity

                              if (tempValue < min || tempValue > max) {
                                return
                              }
                              setRoomSettings((prevValue: any) => {
                                prevValue.options[key].value = tempValue
                                return { ...prevValue }
                              })
                            }}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormLabel>
                      )
                    case 'select':
                      return (
                        <Box key={key} mb="5px">
                          <FormLabel fontWeight={600}>
                            {item.label}{' '}
                            {item.tooltip !== undefined && (
                              <Tooltip label={item.tooltip}>
                                <Icon as={Info} />
                              </Tooltip>
                            )}
                            <Select
                              value={item.value}
                              mb="10px"
                              onChange={(e: any) => {
                                setRoomSettings((prevValue: any) => {
                                  prevValue.options[key].value = e.target.value
                                  return { ...prevValue }
                                })
                              }}
                            >
                              {item.options.map(
                                (
                                  dropdownOption: any,
                                  index: number
                                ): ReactElement => {
                                  return (
                                    <option
                                      key={index}
                                      value={dropdownOption.value}
                                      disabled={dropdownOption.disabled}
                                    >
                                      {dropdownOption.label}
                                    </option>
                                  )
                                }
                              )}
                            </Select>
                          </FormLabel>
                        </Box>
                      )
                    case 'button':
                      return (
                        <Box key={key} mb="5px">
                          <FormLabel fontWeight={600}>
                            {item.label}{' '}
                            {item.tooltip !== undefined && (
                              <Tooltip label={item.tooltip}>
                                <Icon as={Info} />
                              </Tooltip>
                            )}
                            <Button onClick={item.value} mb="10px">
                              {item.buttonLabel}
                            </Button>
                          </FormLabel>
                        </Box>
                      )
                    default:
                  }
                  return <React.Fragment key={index} />
                }
              )}
            </>
          )}
        </DrawerBody>

        <DrawerFooter>
          <Box>
            {download !== null && (
              <>
                <Box
                  as={CSVLink}
                  data={download.data}
                  filename={download.filename}
                  sx={{
                    mb: '20px',
                    display: 'inline-block'
                  }}
                >
                  <DownloadIcon /> Export to CSV
                </Box>
              </>
            )}
            <Box mb="20px">
              {footerLinks.map((item: any, index: number) => {
                const linkAttributes: {
                  href?: string
                  onClick?: any
                } = {}
                if (typeof item.link !== 'undefined') {
                  linkAttributes.href = item.link
                }
                if (typeof item.onClick !== 'undefined') {
                  linkAttributes.onClick = item.onClick
                }
                return (
                  <React.Fragment key={item.label}>
                    <Link whiteSpace="nowrap" {...linkAttributes}>
                      {item.label}
                    </Link>
                    &nbsp;&nbsp;{' '}
                  </React.Fragment>
                )
              })}
            </Box>
            <Flex w="100%" gap="1.5em">
              {socialLinks.map((item: any, index: number) => {
                const linkAttributes: {
                  href?: string
                  onClick?: any
                } = {}
                if (typeof item.link !== 'undefined') {
                  linkAttributes.href = item.link
                }
                return (
                  <Link key={item.name} {...linkAttributes}>
                    <item.icon size="2em" />
                  </Link>
                )
              })}
            </Flex>
          </Box>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default SideNav

const Logo = (): ReactElement => (
  <Box as={Link} href="/">
    <Box as={LogoInline} w="73px" />
  </Box>
)
